/**
 * STRUCT CODE ネイタル診断ページ
 * 生まれ持った本質的な構造を診断
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import structCodeV2Service from '../services/structCodeV2Service';
import { cityData } from '../data/cityData';

const PageContainer = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 24px 16px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-bottom: 40px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 32px;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${props => props.theme.colors.text};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  line-height: 1.2;

  svg {
    color: ${props => props.theme.colors.starGold};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 8px;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.85;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${props => props.theme.colors.borderLight};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: 40px;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${props => props.theme.colors.gradGold};
  border-radius: ${props => props.theme.borderRadius.full};
  box-shadow: 0 0 8px rgba(230,199,126,0.35);
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 40px;
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: 24px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 28px;
    margin-bottom: 20px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 20px;
    margin-bottom: 16px;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.4rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 20px;
    gap: 10px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  font-size: 0.95rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s;
  font-family: ${props => props.theme.fonts.main};
  background: ${props => props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  appearance: none;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryGlow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option { background: ${props => props.theme.colors.bgPrimary}; }
`;

const DateSelectContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 8px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const HelpText = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 6px;
`;

const QuestionCard = styled(Card)`
  padding: 24px;
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px;
  }
`;

const QuestionText = styled.div`
  font-size: 1.05rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-bottom: 16px;
  line-height: 1.6;
`;

const ChoicesContainer = styled.div`
  display: grid;
  gap: 12px;
`;

const ChoiceButton = styled(motion.button)`
  width: 100%;
  padding: 16px 20px;
  text-align: left;
  background: ${props => props.selected
    ? props.theme.colors.primaryGlow
    : props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.selected
    ? props.theme.colors.primary
    : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.25s ease;
  font-size: 0.92rem;
  font-family: ${props => props.theme.fonts.main};
  line-height: 1.65;
  box-shadow: ${props => props.selected ? '0 0 18px rgba(230,199,126,0.18)' : 'none'};

  &:hover {
    border-color: ${props => props.selected ? props.theme.colors.primary : props.theme.colors.borderHover};
    background: ${props => props.selected ? props.theme.colors.primaryGlow : props.theme.colors.bgHover};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
  }
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 15px 30px;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props => props.primary ? `
    background: ${props.theme.colors.gradGold};
    color: #1A1408;
    border: 1px solid rgba(230,199,126,0.55);
    box-shadow: ${props.theme.shadows.glowSoft};

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: ${props.theme.shadows.glow};
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.textSecondary};
    border: 1px solid ${props.theme.colors.lineSoft};

    &:hover {
      color: ${props.theme.colors.starGold};
      border-color: ${props.theme.colors.lineGold};
    }
  `}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: 48px;
  text-align: center;
  max-width: 400px;
  box-shadow: ${props => props.theme.shadows.xl};
  margin: 0 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 32px 24px;
    max-width: 320px;
  }
`;

const LoadingText = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-top: 24px;
`;

const LoadingSubText = styled.div`
  font-size: 0.92rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 14px;
  line-height: 1.85;
`;

// 質問データはAPIから動的に取得します

// 都道府県リスト
const prefectures = Object.keys(cityData);

// 年のオプションを生成（1920年から現在まで）
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 1920; year--) {
    years.push(year);
  }
  return years;
};

// 月のオプション
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

// 日のオプションを生成（選択された年月に応じて）
const generateDayOptions = (year, month) => {
  if (!year || !month) return Array.from({ length: 31 }, (_, i) => i + 1);
  const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
};

function DiagnosisV2Page() {
  const navigate = useNavigate();
  const [step, setStep] = useState('info'); // 'info', 'questions', 'submitting'
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthPrefecture, setBirthPrefecture] = useState('');
  const [birthCity, setBirthCity] = useState('');
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [, setLoadingQuestions] = useState(true);

  // 質問データをAPIから取得
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoadingQuestions(true);
        // 環境変数またはデフォルトのAPIエンドポイントを使用
        const apiUrl = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';
        console.log('Fetching questions from:', `${apiUrl}/api/questions`);
        const response = await axios.get(`${apiUrl}/api/questions`);

        // APIから取得したデータを期待する形式に変換
        const transformedQuestions = response.data.map(q => ({
          id: q.id,
          text: q.question, // 'question'フィールドを'text'に変換
          choices: Object.entries(q.choices).map(([key, value]) => ({
            id: key,
            text: value
          }))
        }));

        setQuestions(transformedQuestions);
      } catch (error) {
        console.error('質問データの取得に失敗しました:', error);
        console.error('API URL:', process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app');
        // エラー時はエラーメッセージを表示し、再試行できるようにする
        alert('質問データの取得に失敗しました。バックエンドサーバー(https://struct-code-production.up.railway.app)が起動しているか確認してください。');
        setQuestions([]);
      } finally {
        setLoadingQuestions(false);
      }
    };
    fetchQuestions();
  }, []);

  const handlePrefectureChange = (e) => {
    setBirthPrefecture(e.target.value);
    setBirthCity(''); // Reset city when prefecture changes
  };

  const progress = step === 'info'
    ? 0
    : ((currentQuestion + 1) / questions.length) * 100;

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (birthYear && birthMonth && birthDay && birthPrefecture && birthCity) {
      setStep('questions');
    }
  };

  const handleChoiceSelect = (choice) => {
    setAnswers({
      ...answers,
      [questions[currentQuestion].id]: choice
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // 全質問完了 - 診断実行
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // 回答を配列形式に変換
      const answersArray = Object.entries(answers).map(([questionId, choice]) => ({
        question_id: questionId,
        choice: choice
      }));

      // 生年月日を YYYY-MM-DD 形式にフォーマット
      const birthDate = `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`;

      // 出生地の文字列を構成（市区町村名, 都道府県名）
      const birthLocation = `${birthCity}, ${birthPrefecture}`;

      // 診断リクエスト
      const diagnosisData = {
        birth_date: birthDate,
        birth_location: birthLocation,
        answers: answersArray,
      };

      const result = await structCodeV2Service.createDiagnosis(diagnosisData);

      // 結果ページへ遷移
      navigate(`/natal-result/${result.diagnosis_id}`, {
        state: { diagnosis: result }
      });
    } catch (error) {
      console.error('診断に失敗しました:', error);
      alert(`診断に失敗しました: ${error.message || '不明なエラー'}`);
      setIsSubmitting(false);
    }
  };

  const isCurrentQuestionAnswered = answers[questions[currentQuestion]?.id] !== undefined;

  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Header>
        <Title>
          <Sparkles size={32} />
          ネイタル診断
        </Title>
        <Subtitle>
          生まれ持った本質的な内面構造を診断します
        </Subtitle>
      </Header>

      <ProgressBar>
        <ProgressFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </ProgressBar>

      <AnimatePresence mode="wait">
        {step === 'info' && (
          <Card
            key="info"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <SectionTitle>
              <Calendar size={24} />
              出生情報の入力
            </SectionTitle>

            <form onSubmit={handleInfoSubmit}>
              <FormGroup>
                <Label htmlFor="birthDate">生年月日 *</Label>
                <DateSelectContainer>
                  <Select
                    id="year"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    required
                  >
                    <option value="">年</option>
                    {generateYearOptions().map(y => (
                      <option key={y} value={y}>{y}年</option>
                    ))}
                  </Select>

                  <Select
                    id="month"
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    required
                  >
                    <option value="">月</option>
                    {MONTH_OPTIONS.map(m => (
                      <option key={m} value={m}>{m}月</option>
                    ))}
                  </Select>

                  <Select
                    id="day"
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    required
                  >
                    <option value="">日</option>
                    {generateDayOptions(birthYear, birthMonth).map(d => (
                      <option key={d} value={d}>{d}日</option>
                    ))}
                  </Select>
                </DateSelectContainer>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="prefecture">出生地 *</Label>
                <FormRow>
                  <Select
                    id="prefecture"
                    value={birthPrefecture}
                    onChange={handlePrefectureChange}
                    required
                  >
                    <option value="">都道府県を選択</option>
                    {prefectures.map(pref => (
                      <option key={pref} value={pref}>{pref}</option>
                    ))}
                  </Select>

                  <Select
                    id="city"
                    value={birthCity}
                    onChange={(e) => setBirthCity(e.target.value)}
                    disabled={!birthPrefecture}
                    required
                  >
                    <option value="">市区町村を選択</option>
                    {birthPrefecture && cityData[birthPrefecture] && cityData[birthPrefecture].map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </Select>
                </FormRow>
                <HelpText>
                  <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  出生時の所在地を選択してください
                </HelpText>
              </FormGroup>

              <Button type="submit" primary>
                次へ（質問へ進む）
                <ArrowRight size={20} />
              </Button>
            </form>
          </Card>
        )}

        {step === 'questions' && (
          <div key="questions">
            <QuestionCard
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <QuestionText>
                <strong>Q.{currentQuestion + 1}</strong> / {questions.length}
                <br />
                {questions[currentQuestion].text}
              </QuestionText>

              <ChoicesContainer>
                {questions[currentQuestion].choices.map((choice) => (
                  <ChoiceButton
                    key={choice.id}
                    selected={answers[questions[currentQuestion].id] === choice.id}
                    onClick={() => handleChoiceSelect(choice.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {choice.id}. {choice.text}
                  </ChoiceButton>
                ))}
              </ChoicesContainer>
            </QuestionCard>

            <NavigationButtons>
              {currentQuestion > 0 && (
                <Button onClick={handlePrevious}>
                  前の質問
                </Button>
              )}
              <Button
                primary
                onClick={handleNext}
                disabled={!isCurrentQuestionAnswered}
              >
                {currentQuestion < questions.length - 1 ? '次の質問' : '診断を実行'}
                <ArrowRight size={20} />
              </Button>
            </NavigationButtons>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSubmitting && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={48} color="#6366F1" />
              </motion.div>
              <LoadingText>診断を実行中...</LoadingText>
              <LoadingSubText>
                正確な天体計算を行っています。<br />
                少々お待ちください。
              </LoadingSubText>
            </LoadingCard>
          </LoadingOverlay>
        )}
      </AnimatePresence>
    </PageContainer>
  );
}

export default DiagnosisV2Page;
